import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, ActionButton, PillBadge } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { roleLabel } from "../lib/roles";
import { AtSign } from "lucide-react";
import { NotificationPrefsCard, MutedSitesCard } from "../components/NotificationPrefsCard";

export default function Profile() {
  const { user, refreshUser } = useAuth();

  return (
    <div>
      <PageHeader title="Profile" subtitle="Your notification preferences and account details." />
      <div className="p-8 space-y-6 max-w-3xl">
        <div className="border border-[#27272A] bg-[#121212] p-5" data-testid="profile-account-card">
          <div className="label-mute mb-3">Account</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="label-mute">Name</div><div className="font-medium">{user?.name || "—"}</div></div>
            <div><div className="label-mute">Email</div><div className="font-mono text-xs">{user?.email || "—"}</div></div>
            <div><div className="label-mute">Role</div><PillBadge tone="blue">{roleLabel(user?.role)}</PillBadge></div>
            <div>
              <div className="label-mute">Username</div>
              <div className="font-mono text-xs">{user?.username || <span className="text-zinc-500 italic normal-case tracking-normal">not set</span>}</div>
            </div>
          </div>
        </div>

        <UsernameCard user={user} refreshUser={refreshUser} />

        <div data-testid="profile-notifications-card">
          <NotificationPrefsCard />
        </div>

        <MutedSitesCard />
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Username alias — per-agency login alias in addition to email.
// Requires password re-entry to prevent session-hijack ATO.
// ─────────────────────────────────────────────────────────────────
function UsernameCard({ user, refreshUser }) {
  const current = user?.username || "";
  const [draft, setDraft] = useState(current);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  // Officers' login handles are admin-managed (created via the Officers page),
  // so we render the card in read-only mode with a friendly note.
  const isGuard = user?.role === "guard";

  useEffect(() => { setDraft(user?.username || ""); }, [user?.username]);

  const dirty = draft !== current;
  const isClearing = current && !draft.trim();

  const save = async () => {
    if (!password) { toast.error("Enter your current password to authorise the change."); return; }
    setSaving(true);
    try {
      await api.post("/auth/username", { username: draft.trim() || null, password });
      toast.success(isClearing ? "Username cleared" : "Username saved");
      setPassword("");
      await refreshUser?.();
    } catch (e) { toast.error(errorMessage(e, "Could not update username")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#27272A] bg-[#121212]" data-testid="profile-username-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <AtSign className="size-4 text-[#FF3B30]" />
        <div>
          <div className="label-mute">Login</div>
          <div className="font-display uppercase font-bold text-lg">Username alias</div>
        </div>
      </div>
      {isGuard ? (
        <div className="px-5 py-4 space-y-2">
          <div className="text-xs text-zinc-400 leading-snug">
            Your login handle is <span className="font-mono text-zinc-200">{current || "—"}</span>. Officer logins are managed by your admin — reach out to them to change it.
          </div>
        </div>
      ) : (
        <>
          <div className="px-5 py-4 space-y-3">
            <div className="text-xs text-zinc-400 leading-snug">
              Pick a short handle to log in with instead of your email. Case-insensitive, unique inside your agency, 3–32 letters/numbers/underscore/dot.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="label-mute block mb-1" htmlFor="username-input">Username</label>
                <input
                  id="username-input"
                  data-testid="profile-username-input"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="e.g. ops.mike"
                  maxLength={32}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] px-3 py-2 text-sm font-mono outline-none"
                />
              </div>
              <div>
                <label className="label-mute block mb-1" htmlFor="username-password">Current password</label>
                <input
                  id="username-password"
                  data-testid="profile-username-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Required to save"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] px-3 py-2 text-sm outline-none"
                />
              </div>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-[#27272A] flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
            <div>{current ? `Current: ${current}` : "No username set"}{isClearing && " · will clear on save"}</div>
            <ActionButton
              data-testid="profile-username-save"
              onClick={save}
              disabled={saving || !dirty || !password}
            >
              {saving ? "Saving…" : isClearing ? "Clear username" : "Save username"}
            </ActionButton>
          </div>
        </>
      )}
    </div>
  );
}
